/**
 * The offering vocabulary, for the UI.
 *
 * These values mirror `@getequity/service-utils` (models/token.schema.js). This app talks to
 * the API over HTTP rather than importing that package, so the duplication is unavoidable —
 * but it is duplication, and it drifts silently. If the backend adds a status, an edge or a
 * policy, it has to be added here too.
 *
 * The safer end state is the API returning the legal next transitions per offering, at which
 * point OFFERING_TRANSITIONS below can be deleted rather than maintained. Worth doing before
 * this map grows.
 */

export const OfferingTypes = Object.freeze({
  FIXED_PRICE: "FIXED_PRICE",
  BOOK_BUILDING: "BOOK_BUILDING",
  AUCTION: "AUCTION",
  COMBINED: "COMBINED",
});

export const OfferingStatus = Object.freeze({
  DRAFT: "DRAFT",
  INTERNAL_REVIEW: "INTERNAL_REVIEW",
  ADMIN_REVIEW: "ADMIN_REVIEW",
  APPROVED: "APPROVED",
  SCHEDULED: "SCHEDULED",
  OPEN: "OPEN",
  CLOSED: "CLOSED",
  PRICED: "PRICED",
  ALLOTMENT_PROPOSED: "ALLOTMENT_PROPOSED",
  ALLOTMENT_APPROVED: "ALLOTMENT_APPROVED",
  SETTLING: "SETTLING",
  LISTED: "LISTED",
  CANCELLED: "CANCELLED",
  EXITED: "EXITED",
});

/**
 * What a winning auction bidder actually pays.
 *
 * UNIFORM: every winner settles at the cut-off, however far above it they bid. Bidding
 * aggressively costs nothing extra, so it draws out what people will really pay.
 * MULTIPLE: every winner settles at their own bid ("pay your bid"), so an aggressive bid is
 * paid for in full.
 *
 * Auctions only. The API forbids the field on any other offering type rather than ignoring it,
 * because a book build clears at one price by construction and a fixed-price deal has no bids
 * to pay.
 */
export const AuctionPricing = Object.freeze({
  UNIFORM: "UNIFORM",
  MULTIPLE: "MULTIPLE",
});

export const AUCTION_PRICING_LABELS = Object.freeze({
  [AuctionPricing.UNIFORM]: "Everyone pays the cut-off price",
  [AuctionPricing.MULTIPLE]: "Everyone pays their own bid",
});

export const AUCTION_PRICING_HINTS = Object.freeze({
  [AuctionPricing.UNIFORM]:
    "Winning bidders all settle at the lowest accepted price, whatever they bid. Bidding high costs nothing extra, so investors bid closer to their real limit.",
  [AuctionPricing.MULTIPLE]:
    "Each winning bidder settles at the price they named. This can raise more, but investors bid cautiously because a high bid is one they have to pay.",
});

/**
 * How a sponsor pays to list, which decides WHEN they pay.
 *
 * Values are capitalised rather than upper case because that is what the API stores. Only the
 * shape is the sponsor's choice; the rate itself is the platform's.
 */
export const ListingFeeType = Object.freeze({
  FLAT: "Flat",
  PERCENTAGE: "Percentage",
});

export const LISTING_FEE_TYPE_LABELS = Object.freeze({
  [ListingFeeType.FLAT]: "A flat charge, paid before the offer goes live",
  [ListingFeeType.PERCENTAGE]: "A percentage, taken from the proceeds at settlement",
});

export const LISTING_FEE_TYPE_HINTS = Object.freeze({
  [ListingFeeType.FLAT]:
    "You pay a set amount from your organisation's balance. The offer cannot be approved until it is settled, so this is due up front whatever the raise turns out to be.",
  [ListingFeeType.PERCENTAGE]:
    "A share of what you actually raise, taken out of the proceeds when the offer settles. Nothing is due up front, and the amount is only an estimate until the book closes.",
});

export const AllotmentMethods = Object.freeze({
  PRO_RATA: "PRO_RATA",
  FCFS: "FCFS",
  PRICE_PRIORITY: "PRICE_PRIORITY",
});

export const UnderSubscriptionPolicies = Object.freeze({
  PROCEED_REDUCED: "PROCEED_REDUCED",
  EXTEND: "EXTEND",
  CANCEL: "CANCEL",
});

/**
 * Copied from OFFERING_TRANSITIONS in service-utils. Two edges are load-bearing and easy to
 * mistake for errors:
 *
 *   ALLOTMENT_PROPOSED -> PRICED   a rejected allotment can be re-priced
 *   OPEN -> SETTLING               the continuous-fill path, with no allotment in between
 */
export const OFFERING_TRANSITIONS = Object.freeze({
  [OfferingStatus.DRAFT]: [OfferingStatus.INTERNAL_REVIEW, OfferingStatus.ADMIN_REVIEW, OfferingStatus.CANCELLED],
  [OfferingStatus.INTERNAL_REVIEW]: [OfferingStatus.DRAFT, OfferingStatus.ADMIN_REVIEW, OfferingStatus.CANCELLED],
  [OfferingStatus.ADMIN_REVIEW]: [OfferingStatus.DRAFT, OfferingStatus.APPROVED, OfferingStatus.CANCELLED],
  [OfferingStatus.APPROVED]: [OfferingStatus.SCHEDULED, OfferingStatus.OPEN, OfferingStatus.CANCELLED],
  [OfferingStatus.SCHEDULED]: [OfferingStatus.OPEN, OfferingStatus.CANCELLED],
  [OfferingStatus.OPEN]: [OfferingStatus.CLOSED, OfferingStatus.SETTLING, OfferingStatus.CANCELLED],
  [OfferingStatus.CLOSED]: [OfferingStatus.PRICED, OfferingStatus.CANCELLED],
  [OfferingStatus.PRICED]: [OfferingStatus.ALLOTMENT_PROPOSED, OfferingStatus.CANCELLED],
  [OfferingStatus.ALLOTMENT_PROPOSED]: [
    OfferingStatus.ALLOTMENT_APPROVED,
    OfferingStatus.PRICED,
    OfferingStatus.CANCELLED,
  ],
  [OfferingStatus.ALLOTMENT_APPROVED]: [OfferingStatus.SETTLING, OfferingStatus.CANCELLED],
  [OfferingStatus.SETTLING]: [OfferingStatus.LISTED],
  [OfferingStatus.LISTED]: [OfferingStatus.EXITED],
  [OfferingStatus.CANCELLED]: [],
  [OfferingStatus.EXITED]: [],
});

export const canTransitionOffering = (from, to) =>
  Boolean(from && to && (OFFERING_TRANSITIONS[from] || []).includes(to));

/**
 * Whether a deal opted into an offer period.
 *
 * This is the gate for everything — not the offering type, and not the status. It matches
 * `HAS_OFFER_PERIOD` in Utility/cron/offer-scheduler.js and `hasOfferPeriod()` in
 * Exchange/utils/offering.js, and the three have to agree.
 *
 * @param {object} deal
 * @returns {boolean}
 */
export const hasOfferPeriod = (deal) => Boolean(deal && deal.offer_closes_at);

/**
 * Which object actually carries the live offering state.
 *
 * A sponsor configures the offering on the TokenRequest; approval copies that config onto the
 * Token, and from then on the Token is the one the scheduler moves. The request's copy stops
 * being updated, so a list showing request objects would otherwise freeze at SCHEDULED while
 * the real offering opened and closed.
 *
 * @param {object} deal - a TokenRequest (possibly with a populated `token`) or a Token
 * @returns {object|null}
 */
export const offeringSource = (deal) => {
  if (!deal) return null;
  if (deal.token && hasOfferPeriod(deal.token)) return deal.token;
  return deal;
};

/**
 * Whether this deal is currently taking bids into a book.
 *
 * `offering_status === OPEN` on its own is NOT enough. Deals created since offering statuses
 * were introduced carry OPEN while still filling continuously, exactly as they always have —
 * branching on the status alone puts a bid form in front of a deal that settles instantly.
 *
 * @param {object} deal
 * @returns {boolean}
 */
export const isTakingBids = (deal) => {
  const source = offeringSource(deal);
  return hasOfferPeriod(source) && source.offering_status === OfferingStatus.OPEN;
};

/**
 * Whether this deal fills continuously, the way every deal did before offering types.
 *
 * @param {object} deal
 * @returns {boolean}
 */
export const isContinuousFill = (deal) => !hasOfferPeriod(deal);

/**
 * Whether this deal is on chain at all.
 *
 * Either half is enough, and they answer different questions. `chains` is the sponsor's stated
 * intent and is set the moment they pick one — including on a deal that mints later at
 * settlement, which has no deployments yet and is certainly on chain. `onchain_deployments` is
 * what actually happened, and it carries a chain minted after approval, which is recorded on
 * the token rather than back on the request.
 *
 * Gated on the token existing, not merely on the deal: most deals never go on chain, and
 * reading the arrays alone would count a deal that was never tokenized.
 *
 * Shared by the On-chain tab on a single deal and the On-chain filter over the list, so the
 * two cannot disagree about which deals are on chain. The deal list populates the whole token
 * document, so both arrays are present there as well as on a single deal.
 *
 * @param {object} deal - a TokenRequest with a populated `token`, or a Token
 * @returns {boolean}
 */
export const isDealOnchain = (deal) =>
  Boolean(
    deal?.token
      && (deal?.token?.chains?.length
        || deal?.token?.onchain_deployments?.length
        || deal?.chains?.length),
  );

const toDate = (value) => {
  if (!value) return null;
  const date = value instanceof Date ? value : new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
};

/**
 * A human interval, for countdowns. Deliberately coarse: "closes in 6 hours" is more useful
 * to a sponsor than a ticking clock, and it does not need a timer to stay honest.
 *
 * @param {Date|string} target
 * @param {Date} [now]
 * @returns {string|null}
 */
export const timeUntil = (target, now = new Date()) => {
  const date = toDate(target);
  if (!date) return null;

  const ms = date.getTime() - now.getTime();
  if (ms <= 0) return null;

  const minutes = Math.round(ms / 60000);
  if (minutes < 60) return `${minutes} minute${minutes === 1 ? "" : "s"}`;

  const hours = Math.round(minutes / 60);
  if (hours < 48) return `${hours} hour${hours === 1 ? "" : "s"}`;

  const days = Math.round(hours / 24);
  return `${days} day${days === 1 ? "" : "s"}`;
};

/**
 * Investment types whose guidance numbers are rates rather than prices.
 *
 * Matches the types that already show an "Interest rate (% p.a)" field on the deal form, so
 * guidance is quoted in the same unit as interest — percent, not basis points. The schema
 * stores plain numbers with no unit, so introducing a second convention here would be a
 * silent factor-of-100 waiting to happen.
 */
// Matches isRateBased in the API's commitment service, which is [DEBT, FixedInterest] only.
// Fund used to be in this list, which made the whole app ask for a fund bid as a percentage
// while the API read it as a price and wrote back a final_price.
export const RATE_BASED_INVESTMENT_TYPES = Object.freeze(["Debt", "Fixed Interest"]);

export const isRateBased = (investmentType) =>
  RATE_BASED_INVESTMENT_TYPES.includes(investmentType);

/**
 * Labels for the guidance range.
 *
 * Kept short on purpose: these render as floating placeholders that sit inside the input and
 * shrink to a chip on the border when focused, so a sentence overflows the field. The meaning
 * that will not fit — which end the issuer actually wants — goes in guidanceHint() below,
 * under the pair, where there is room for it.
 *
 * The ordering constraint is unaffected by any of this: floor is always the smaller number.
 * Do not "correct" the rate inversion by swapping the fields — that stores the range
 * backwards while looking right.
 *
 * @param {string} investmentType
 * @param {string} [currency]
 */
export const guidanceLabels = (investmentType, currency = "") => {
  const unit = isRateBased(investmentType) ? "%" : currency;
  const suffix = unit ? ` (${unit})` : "";

  return isRateBased(investmentType)
    ? {
        floor: `Lowest rate${suffix}`,
        cap: `Highest rate${suffix}`,
        step: `Bid increment${suffix}`,
        reserve: `Reserve rate${suffix}`,
      }
    : {
        floor: `Lowest price${suffix}`,
        cap: `Highest price${suffix}`,
        step: `Bid increment${suffix}`,
        reserve: `Reserve price${suffix}`,
      };
};

/**
 * The tick size, explained. Without one, bids land on arbitrary values and the demand ladder
 * becomes hundreds of levels holding a sliver each — which is unreadable at exactly the moment
 * the sponsor has to price against it.
 *
 * Rates are quoted in percent to match the deal form's existing interest field, so a sponsor
 * thinking in basis points has to type 0.25 for 25bps. The worked example is there to make
 * that obvious before they type 25.
 */
export const stepHint = (investmentType) =>
  isRateBased(investmentType)
    ? "Bids must land on a multiple of this. Enter 0.25 for quarter-point steps, so bids land at 12.00%, 12.25%, 12.50%. Leave blank to accept any rate in the range."
    : "Bids must land on a multiple of this. Leave blank to accept any price in the range.";

/**
 * What the range means, spelled out. An equity issuer wants a high price, so the cap is the
 * good end; a debt issuer wants a low rate, so the floor is. "Lowest" and "highest" alone
 * read backwards on half our deals, so this carries the direction instead.
 */
export const guidanceHint = (investmentType) =>
  isRateBased(investmentType)
    ? "The range of rates you are willing to pay. Bids at or below your final rate will be eligible for allotment."
    : "The range investors can bid within. Bids at or above your final price will be eligible for allotment.";

/**
 * The eligibility rule on its own, for places that already have the range in view.
 */
export const eligibilityHint = (investmentType) =>
  isRateBased(investmentType)
    ? "Bids at or below your final rate will be eligible for allotment."
    : "Bids at or above your final price will be eligible for allotment.";

/**
 * Auctions allot strictly best-to-worst, so the method is not a choice. Mirrors the
 * `allotment_method` rule in the API's offering validation.
 */
export const allotmentMethodsFor = (offeringType) =>
  offeringType === OfferingTypes.AUCTION
    ? [AllotmentMethods.PRICE_PRIORITY]
    : [AllotmentMethods.PRO_RATA, AllotmentMethods.FCFS];

export const ALLOTMENT_METHOD_LABELS = Object.freeze({
  [AllotmentMethods.PRO_RATA]: "Pro-rata",
  [AllotmentMethods.FCFS]: "First come, first served",
  [AllotmentMethods.PRICE_PRIORITY]: "Price priority",
});

export const UNDERSUBSCRIPTION_LABELS = Object.freeze({
  [UnderSubscriptionPolicies.PROCEED_REDUCED]: "Proceed at reduced size",
  [UnderSubscriptionPolicies.EXTEND]: "Extend the offer period",
  [UnderSubscriptionPolicies.CANCEL]: "Cancel and return funds",
});

const toNumber = (value) => {
  if (value === null || value === undefined || value === "") return null;
  const n = Number(String(value).replace(/,/g, ""));
  return Number.isFinite(n) ? n : null;
};

/**
 * The offering types that can be sold in slices.
 *
 * Mirrors TRANCHED_OFFERINGS in the API. A combined offering IS its tranches and means nothing
 * without them. A fixed-price one MAY have them: a commercial paper issued as 90/180/270-day
 * series is one raise with three sets of terms, and its books only size the scale-back.
 *
 * A book build or an auction discovers a single level across one book, so a tranche on one
 * describes a structure that would never be run — and the API refuses the field there.
 */
export const TRANCHED_OFFERINGS = Object.freeze([
  OfferingTypes.COMBINED,
  OfferingTypes.FIXED_PRICE,
]);

export const canBeTranched = (offeringType) => TRANCHED_OFFERINGS.includes(offeringType);

/**
 * Whether this offering is actually sliced.
 *
 * The question almost everything asks, and deliberately not "is it combined": a slice is sized,
 * bid into and allotted the same way whatever put it there. The one thing that differs is where
 * the levels come from, which is `statesTrancheLevels` below.
 */
export const hasTranches = (state) =>
  Array.isArray(state?.tranches) && state.tranches.length > 0;

/**
 * Whether a slice STATES its own level rather than discovering one from its book.
 *
 * A fixed-price offering has no book to discover from — the issuer set the numbers — so its
 * slices carry their own. A combined offering finds one per slice at pricing, and the API
 * refuses a stated level there rather than overwriting it silently.
 */
export const statesTrancheLevels = (offeringType) =>
  offeringType === OfferingTypes.FIXED_PRICE;

/**
 * Who a tranche will take bids from.
 *
 * Mirrors TrancheInvestors in the API. Deliberately an account-kind check rather than an
 * investor classification: there is no accreditation status on the platform to test against,
 * so a tranche claiming to be "institutional only" would be enforcing nothing.
 */
export const TrancheInvestors = Object.freeze({
  ANY: "ANY",
  USER: "user",
  ORGANISATION: "organisation",
});

export const TRANCHE_INVESTOR_LABELS = Object.freeze({
  [TrancheInvestors.ANY]: "Anyone",
  [TrancheInvestors.USER]: "Individual investors only",
  [TrancheInvestors.ORGANISATION]: "Organisations only",
});

/**
 * A URL-safe key derived from the tranche's name.
 *
 * The key is what commitments and allotment lines point at, so it is generated ONCE when a
 * tranche is created and never re-derived. Renaming "Retail" to "Retail Tranche" after bids
 * exist must not move those bids into a tranche nobody bid on.
 *
 * @param {string} name
 * @param {string[]} taken keys already in use on this offering
 * @returns {string}
 */
export const makeTrancheKey = (name, taken = []) => {
  const base = String(name || "")
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 40) || "tranche";

  if (!taken.includes(base)) return base;
  // Suffixed rather than rejected: two slices legitimately called "Series 1" in different
  // currencies is a naming problem for the sponsor, not a reason to refuse the form.
  let n = 2;
  while (taken.includes(`${base}-${n}`)) n += 1;
  return `${base}-${n}`;
};

/**
 * A new, empty tranche row.
 *
 * `investors` is set rather than left blank so the payload always states an audience, matching
 * the API's own default.
 */
export const blankTranche = () => ({
  key: "",
  name: "",
  size: null,
  allotment_method: null,
  guidance_floor: null,
  guidance_cap: null,
  guidance_step: null,
  reserve_price: null,
  min_commitment: null,
  max_commitment: null,
  investors: TrancheInvestors.ANY,
  tenor: null,
  // What this slice pays, on an offering that states its terms rather than discovering them.
  // One field rather than the API's rate/price pair: which of the two it becomes is decided by
  // the instrument at build time, so the form cannot produce the crossed pair the API refuses.
  level: null,
});

/**
 * One tranche, flattened into the shape the API takes.
 *
 * Every optional field is omitted rather than sent as null: a tranche that states nothing of
 * its own inherits the offering's bounds, and sending explicit nulls would overwrite that
 * inheritance with "no guidance at all".
 *
 * @param {object} row
 * @param {object} [options]
 * @param {boolean} [options.statesLevels] whether this offering type states its own levels
 * @param {boolean} [options.rateBased] whether this instrument's level is a rate, not a price
 * @returns {object}
 */
const buildTranche = (row, { statesLevels = false, rateBased = false } = {}) => {
  const tranche = {
    key: row.key || makeTrancheKey(row.name),
    name: String(row.name || "").trim(),
    size: toNumber(row.size),
  };

  const floor = toNumber(row.guidance_floor);
  const cap = toNumber(row.guidance_cap);
  const step = toNumber(row.guidance_step);
  // Both or neither, exactly as the offering's own guidance works.
  if (floor !== null && cap !== null) {
    tranche.price_guidance = { floor, cap };
    if (step !== null) tranche.price_guidance.step = step;
  }

  const reserve = toNumber(row.reserve_price);
  if (reserve !== null) tranche.reserve_price = reserve;

  const min = toNumber(row.min_commitment);
  if (min !== null) tranche.min_commitment = min;
  const max = toNumber(row.max_commitment);
  if (max !== null) tranche.max_commitment = max;

  if (row.allotment_method) tranche.allotment_method = row.allotment_method;
  // ANY is the API's own default, so sending it adds a field to say what its absence says.
  if (row.investors && row.investors !== TrancheInvestors.ANY) {
    tranche.investors = row.investors;
  }

  const tenor = toNumber(row.tenor);
  if (tenor !== null) tranche.tenor = tenor;

  // A rate on debt, a price on equity — never both, and never on a type that discovers its own.
  // The API rejects the wrong side rather than ignoring it, and a slice that states nothing
  // falls back to the deal's level, so an unset field is omitted rather than sent as null.
  const level = toNumber(row.level);
  if (statesLevels && level !== null) {
    if (rateBased) tranche.rate = level;
    else tranche.price = level;
  }

  return tranche;
};

/**
 * The offering fields to send, and only those.
 *
 * The API *rejects* fields an offering type can't use rather than ignoring them — guidance on
 * a fixed-price deal, a reserve on anything but an auction, strike bids on anything but a book
 * build. So hiding a field in the template is not enough; it has to be absent from the
 * payload. This is the one place that decides, so a new field can't be added to the form and
 * quietly leak into every request.
 *
 * @param {object} state
 * @param {string} [investmentType] decides whether a stated tranche level is a rate or a price
 * @returns {object} only the applicable offering keys
 */
export const buildOfferingPayload = (state = {}, investmentType = "") => {
  const type = state.offering_type || OfferingTypes.FIXED_PRICE;
  const payload = { offering_type: type };
  const sliced = canBeTranched(type) && hasTranches(state);

  // An unsliced fixed-price deal keeps today's behaviour: no window, so no allotment and no
  // close-time policy. Sliced, it needs all three — the sizes are what a scale-back is measured
  // against and a scale-back only happens at a close, which is why the API makes
  // offer_closes_at required the moment tranches are present rather than keying it on the type.
  if (type === OfferingTypes.FIXED_PRICE && !sliced) return payload;

  if (state.offer_opens_at) payload.offer_opens_at = state.offer_opens_at;
  if (state.offer_closes_at) payload.offer_closes_at = state.offer_closes_at;

  // Allotment is what happens *at* a close, so a method without one describes a step that
  // never runs — the API rejects that pairing.
  if (state.offer_closes_at && state.allotment_method) {
    payload.allotment_method = state.allotment_method;
  }
  if (state.offer_closes_at && state.undersubscription_policy) {
    payload.undersubscription_policy = state.undersubscription_policy;
  }

  if (type === OfferingTypes.BOOK_BUILDING) {
    const floor = toNumber(state.guidance_floor);
    const cap = toNumber(state.guidance_cap);
    const step = toNumber(state.guidance_step);

    // Both or neither: a floor without a cap is not a range, and the API enforces it.
    if (floor !== null && cap !== null) {
      payload.price_guidance = { floor, cap };
      if (step !== null) payload.price_guidance.step = step;
      // Written only to unenforce. Absent already means enforced on the API, so sending true
      // would add a field to every offering to say what its absence says.
      if (state.guidance_enforced === false) payload.price_guidance.enforced = false;
    }
    payload.allow_strike_bids = Boolean(state.allow_strike_bids);
  }

  // Sent on every offering type. It is a commercial term of listing rather than part of how
  // the offer prices, so unlike the fields below it is not gated on the offering type.
  if (state.listing_fee_type) {
    payload.listing_fee_type = state.listing_fee_type;
  }

  // The slices. Required by the API on a combined offering — which is why an empty set is still
  // sent for that type, so the failure is the API's own message rather than a missing field —
  // and optional on a fixed-price one. Refused on every other type, so it is gated on the type
  // as well as on the rows being there.
  if (type === OfferingTypes.COMBINED || sliced) {
    payload.tranches = (state.tranches || []).map((row) =>
      buildTranche(row, {
        statesLevels: statesTrancheLevels(type),
        rateBased: isRateBased(investmentType),
      })
    );
  }

  if (type === OfferingTypes.AUCTION) {
    const reserve = toNumber(state.reserve_price);
    if (reserve !== null) payload.reserve_price = reserve;
    // Only inside this branch: the API forbids the field on every other offering type rather
    // than ignoring it, so sending it on a book build is a rejected request, not a stray key.
    payload.auction_pricing = state.auction_pricing || AuctionPricing.UNIFORM;
  }

  return payload;
};

/**
 * Cash comparisons tolerate half a kobo, the same slack the API's tranche guard uses.
 *
 * Sizes are entered to two decimals and summed, so an exact equality against the raise amount
 * would reject a set that is correct to the last kobo.
 */
const CASH_DUST = 0.01;

/**
 * The rules a set of tranches has to satisfy, run in front of the sponsor.
 *
 * Mirrors `assertTranchesAreCoherent` in the API. Every one of these fails at submit anyway —
 * the point is that it fails here first, against the row that caused it, rather than as one
 * Joi sentence about a payload the sponsor never saw.
 *
 * Errors are addressed either to the set (`tranches`) or to a row (`tranches[1].size`), so the
 * builder can put each message under the field that caused it.
 *
 * @param {object} state the form's offering slice
 * @param {string} [investmentType]
 * @returns {Array<{ field: string, message: string }>}
 */
const validateTranches = (state, investmentType = "", offeringType = OfferingTypes.COMBINED) => {
  const rows = state.tranches || [];
  const errors = [];

  if (!rows.length) {
    return [{
      field: "tranches",
      message: statesTrancheLevels(offeringType)
        ? "A sliced offering needs at least one tranche. Turn slicing off to sell it in one piece."
        : "A combined offering needs at least one tranche.",
    }];
  }

  const noun = isRateBased(investmentType) ? "rate" : "price";
  // Only a type that states its terms has a level per slice to check. On a combined offering
  // the field is not offered and the API refuses it outright, so there is nothing to mirror.
  const statesLevels = statesTrancheLevels(offeringType);

  rows.forEach((row, i) => {
    const at = (field) => `tranches[${i}].${field}`;

    if (!String(row.name || "").trim()) {
      errors.push({ field: at("name"), message: "Give this tranche a name." });
    }

    const size = toNumber(row.size);
    if (size === null || size <= 0) {
      errors.push({ field: at("size"), message: "Set a size greater than zero." });
    }

    const min = toNumber(row.min_commitment);
    const max = toNumber(row.max_commitment);
    // A floor above the ceiling admits nobody, and is far easier to write than to notice.
    if (min !== null && max !== null && min > max) {
      errors.push({
        field: at("max_commitment"),
        message: "This tranche accepts nothing: its smallest bid is above its largest.",
      });
    }

    const floor = toNumber(row.guidance_floor);
    const cap = toNumber(row.guidance_cap);
    // Both or neither. One end of a range is not a range, and the API refuses the pair.
    if ((floor === null) !== (cap === null)) {
      errors.push({
        field: at(floor === null ? "guidance_floor" : "guidance_cap"),
        message: `Set both ends of the ${noun} range, or neither and inherit the offering's.`,
      });
    } else if (floor !== null && cap !== null && cap <= floor) {
      errors.push({
        field: at("guidance_cap"),
        message: `The highest ${noun} must be above the lowest.`,
      });
    }

    const tenor = toNumber(row.tenor);
    if (tenor !== null && tenor <= 0) {
      errors.push({ field: at("tenor"), message: "A tenor is a number of days, greater than zero." });
    }

    const level = toNumber(row.level);
    if (statesLevels && level !== null && level <= 0) {
      errors.push({
        field: at("level"),
        message: `A ${noun} of zero would allot this tranche at nothing. Set one above zero.`,
      });
    }
  });

  // Keys are generated from names, so a duplicate key means two tranches share a name — and
  // commitments are routed by key, so the second would collect the first's bids.
  const keys = rows.map((row) => row.key).filter(Boolean);
  if (new Set(keys).size !== keys.length) {
    errors.push({
      field: "tranches",
      message: "Two tranches cannot share a name: bids are routed by it.",
    });
  }

  // The one that matters most. A combined offering is allotted one tranche at a time against
  // that tranche's own size, so sizes that do not add up mean the offering either cannot sell
  // what it advertised or sells more than it has — and neither is visible until settlement.
  const raise = toNumber(state.raise_amount);
  const sized = rows.map((row) => toNumber(row.size)).filter((n) => n !== null);
  if (raise !== null && sized.length === rows.length) {
    const total = Number(sized.reduce((sum, n) => sum + n, 0).toFixed(2));
    if (Math.abs(total - raise) > CASH_DUST) {
      const over = total > raise;
      errors.push({
        field: "tranches",
        message: `The tranches add up to ${total.toLocaleString()}, which is ${
          over ? "more" : "less"
        } than the ${raise.toLocaleString()} you are raising. ${
          over ? "Reduce" : "Increase"
        } them by ${Math.abs(Number((total - raise).toFixed(2))).toLocaleString()}.`,
      });
    }
  }

  // All or none. A tranche with no life of its own inherits the deal's maturity silently, so a
  // sponsor who filled in two of three series would never be told about the third.
  const withTenor = rows.filter((row) => toNumber(row.tenor) !== null);
  if (withTenor.length > 0 && withTenor.length !== rows.length) {
    errors.push({
      field: "tranches",
      message:
        "Either every tranche states its own tenor, or none does. A tranche without one matures with the deal.",
    });
  }

  // The same rule for the same reason, and the API's own assertTrancheLevels: a slice that
  // states no level of its own pays the deal's, silently, so a sponsor who filled in two of
  // three series would find out at settlement rather than here.
  if (statesLevels) {
    const withLevel = rows.filter((row) => toNumber(row.level) !== null);
    if (withLevel.length > 0 && withLevel.length !== rows.length) {
      errors.push({
        field: "tranches",
        message: `Either every tranche states its own ${noun}, or none does. A tranche without one pays the deal's.`,
      });
    }
  }

  return errors;
};

/**
 * Cross-field rules for the offering block.
 *
 * The form validates field by field — each input reports its own validity and the parent
 * counts booleans — which cannot express "cap must exceed floor" or "close must follow open".
 * Those currently only fail at the API, after a submit, with a Joi message written for a
 * developer. This runs the same rules in front of the sponsor instead.
 *
 * Mirrors the API's offering validation rather than inventing rules, with two additions it
 * cannot make: a close date is required for any offering that has to be priced (without one
 * the offering opens and never closes), and a step wider than the range would leave no legal
 * bid.
 *
 * @param {object} state - the form's offering slice
 * @param {string} [investmentType] - lives on the deal, not the offering, but decides whether
 *   the range is quoted as prices or rates and so how these messages read
 * @returns {Array<{ field: string, message: string }>}
 */
export const validateOffering = (state = {}, investmentType = "") => {
  const type = state.offering_type || OfferingTypes.FIXED_PRICE;
  // An unsliced fixed-price deal has nothing here to check: no window, no book, no allotment.
  // Sliced, it acquires all three, so it falls through to the same rules every other type runs.
  if (type === OfferingTypes.FIXED_PRICE && !hasTranches(state)) return [];

  const errors = [];
  const opens = state.offer_opens_at ? new Date(state.offer_opens_at) : null;
  const closes = state.offer_closes_at ? new Date(state.offer_closes_at) : null;

  if (!closes) {
    errors.push({
      field: "offer_closes_at",
      // Two different reasons. Where the level is discovered the close is what makes pricing
      // possible; where it was stated at creation there is nothing to price, and the close is
      // what makes the scale-back the tranche sizes describe possible instead.
      message: statesTrancheLevels(type)
        ? "Set a close date, because a sliced offer has to close before it can be allotted."
        : "Set a close date, because the book has to close before you can price it.",
    });
  }

  if (opens && closes && closes.getTime() <= opens.getTime()) {
    errors.push({
      field: "offer_closes_at",
      message: "The offer must close after it opens.",
    });
  }

  if (closes && !state.allotment_method) {
    errors.push({
      field: "allotment_method",
      message: "Choose how the offer is shared out if demand exceeds it.",
    });
  }

  // Gated on the type as well as the rows: a type that cannot carry slices should be told its
  // own rules are unmet, not handed tranche errors about a structure it will never send.
  if (type === OfferingTypes.COMBINED || (canBeTranched(type) && hasTranches(state))) {
    errors.push(...validateTranches(state, investmentType, type));
  }

  if (type === OfferingTypes.BOOK_BUILDING) {
    const floor = toNumber(state.guidance_floor);
    const cap = toNumber(state.guidance_cap);
    const step = toNumber(state.guidance_step);
    const noun = isRateBased(investmentType) ? "rate" : "price";

    if (floor === null || cap === null) {
      errors.push({
        field: "guidance_floor",
        message: `Book building needs a full range, so set both a lowest and a highest ${noun}.`,
      });
    } else {
      if (floor <= 0) {
        errors.push({
          field: "guidance_floor",
          message: `The lowest ${noun} must be greater than zero.`,
        });
      }
      if (cap <= floor) {
        errors.push({
          field: "guidance_cap",
          message: `The highest ${noun} must be above the lowest.`,
        });
      }
      if (step !== null && step > cap - floor) {
        errors.push({
          field: "guidance_step",
          message: "The increment is wider than the range, so no bid could be valid.",
        });
      }
    }

    if (step !== null && step <= 0) {
      errors.push({
        field: "guidance_step",
        message: "The increment must be greater than zero, or left blank.",
      });
    }
  }

  if (type === OfferingTypes.AUCTION) {
    const reserve = toNumber(state.reserve_price);
    if (reserve !== null && reserve <= 0) {
      errors.push({
        field: "reserve_price",
        message: "The reserve must be greater than zero, or left blank.",
      });
    }
  }

  return errors;
};

const OFFERING_LABELS = {
  [OfferingStatus.DRAFT]: { label: "Draft", tone: "neutral" },
  [OfferingStatus.INTERNAL_REVIEW]: { label: "Awaiting sign-off", tone: "warning" },
  [OfferingStatus.ADMIN_REVIEW]: { label: "In review", tone: "warning" },
  [OfferingStatus.APPROVED]: { label: "Approved", tone: "success" },
  [OfferingStatus.SCHEDULED]: { label: "Scheduled", tone: "info" },
  [OfferingStatus.OPEN]: { label: "Open", tone: "active" },
  [OfferingStatus.CLOSED]: { label: "Book closed", tone: "info" },
  [OfferingStatus.PRICED]: { label: "Priced", tone: "info" },
  [OfferingStatus.ALLOTMENT_PROPOSED]: { label: "Allotment proposed", tone: "warning" },
  [OfferingStatus.ALLOTMENT_APPROVED]: { label: "Allotment approved", tone: "success" },
  [OfferingStatus.SETTLING]: { label: "Settling", tone: "info" },
  [OfferingStatus.LISTED]: { label: "Listed", tone: "success" },
  [OfferingStatus.CANCELLED]: { label: "Cancelled", tone: "neutral" },
  [OfferingStatus.EXITED]: { label: "Exited", tone: "neutral" },
};

/**
 * Request statuses, for deals with no offer period. This is the vocabulary the portal has
 * always shown and it stays exactly as it is.
 */
const REQUEST_LABELS = {
  Pending: { label: "Pending", tone: "warning" },
  Approved: { label: "Approved", tone: "success" },
  Rejected: { label: "Rejected", tone: "danger" },
  Terminated: { label: "Terminated", tone: "neutral" },
};

export const OFFERING_TYPE_LABELS = Object.freeze({
  [OfferingTypes.FIXED_PRICE]: "Fixed price",
  [OfferingTypes.BOOK_BUILDING]: "Book building",
  [OfferingTypes.AUCTION]: "Auction",
  [OfferingTypes.COMBINED]: "Combined",
});

/**
 * The single place that decides which status vocabulary a deal speaks.
 *
 * A deal with an offer period reports its offering status; one without reports its request
 * status, as it always has. Everything user-facing — badges, filters, empty states, gated
 * CTAs — should read this rather than picking a field itself, so there is one answer rather
 * than one per component.
 *
 * @param {object} deal
 * @param {Date} [now]
 * @returns {{
 *   key: string,
 *   label: string,
 *   tone: string,
 *   detail: string|null,
 *   windowed: boolean,
 *   takingBids: boolean
 * }}
 */
export const resolveDealState = (deal, now = new Date()) => {
  const source = offeringSource(deal);
  const windowed = hasOfferPeriod(source);

  if (!windowed) {
    const status = deal?.status;
    const known = REQUEST_LABELS[status];
    return {
      key: status || "Unknown",
      label: known?.label || status || "—",
      tone: known?.tone || "neutral",
      detail: null,
      windowed: false,
      takingBids: false,
    };
  }

  const status = source.offering_status;
  const known = OFFERING_LABELS[status] || { label: status || "—", tone: "neutral" };

  let detail = null;
  if (status === OfferingStatus.SCHEDULED) {
    const until = timeUntil(source.offer_opens_at, now);
    detail = until ? `Opens in ${until}` : "Opening";
  } else if (status === OfferingStatus.OPEN) {
    const until = timeUntil(source.offer_closes_at, now);
    detail = until ? `Closes in ${until}` : "Closing";
  }

  return {
    key: status,
    label: known.label,
    tone: known.tone,
    detail,
    windowed: true,
    takingBids: status === OfferingStatus.OPEN,
  };
};
